import { useEffect } from 'react'
import { Link, Navigate, NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { sessionUnreachable, useSession } from '../../lib/auth.js'
import { SessionUnreachable } from '../../components/FullPageNotice.js'
import { api } from '../../lib/api.js'
import { LocaleProvider } from '../../i18n/index.js'
import { DEFAULT_LOCALE, type Locale } from '@diana/shared'
import { AppFrame } from '../../classroom/Shell.js'
import { Avatar, LogoMark, Skeleton } from '../../classroom/ui.js'
import { Icon, type IconName } from '../../classroom/icons.js'
import { studentCopy } from './copy.js'
import { shellCopy, type ShellStrings } from './copyShell.js'
import { PartBoundary } from './PartBoundary.js'

/**
 * The student's area, in the classroom style (design/style-c).
 *
 * Two deliberate departures from the rest of the app, both recorded in
 * docs and worth knowing before "fixing" either:
 *
 * 1. `/panel` sits OUTSIDE the locale prefix. The public site is the source
 *    of truth for language in the URL; here the language comes from
 *    `users.locale`, because the lesson content itself already carries all
 *    three languages inside it and a prefix would double the route tree for
 *    the chrome alone.
 * 2. Diana is allowed in. She has an ADMIN role, no client card and no
 *    enrollment, so this area is nearly empty for her — but locking her out
 *    of the thing she sells is worse than showing her an empty shelf.
 *
 * Three kinds of chrome, chosen by route: the full sidebar; a 76px icon rail
 * for focused work (flashcards, homework inside a lesson); and none at all
 * for the lesson player, which is a full-screen frame of its own.
 */
type Chrome = 'side' | 'rail' | 'none'

type NavItem = { key: keyof ShellStrings['nav']; tab?: keyof ShellStrings['tabs']; to: string; end: boolean; icon: IconName }

const NAV: NavItem[] = [
  { key: 'home', tab: 'home', to: '/panel', end: true, icon: 'home' },
  { key: 'courses', tab: 'courses', to: '/panel/kurs', end: false, icon: 'book' },
  { key: 'homework', tab: 'homework', to: '/panel/zadania', end: false, icon: 'homework' },
  { key: 'review', tab: 'review', to: '/panel/powtorki', end: false, icon: 'cards' },
  { key: 'glossary', to: '/panel/slownik', end: false, icon: 'search' },
  { key: 'lessons', tab: 'lessons', to: '/panel/lekcje', end: false, icon: 'calendar' },
]

export function StudentLayout() {
  const sessionState = useSession()
  const { data: session, isPending } = sessionState
  const unreachable = sessionUnreachable(sessionState)
  const navigate = useNavigate()
  const location = useLocation()

  const user = session?.user as { name?: string; locale?: string; role?: string } | undefined
  const locale = (user?.locale ?? DEFAULT_LOCALE) as Locale
  const t = studentCopy(locale)
  const s = shellCopy(locale)
  // Diana comes here from the studio to see the course as a student does; give her the way back.
  const isAdmin = user?.role === 'ADMIN'

  const inPlayer = useMatch('/panel/kurs/:slug/:lessonSlug')
  const inLessonHomework = useMatch('/panel/kurs/:slug/:lessonSlug/zadanie')
  const inReview = useMatch('/panel/powtorki')
  const chrome: Chrome = inPlayer ? 'none' : inLessonHomework || inReview ? 'rail' : 'side'

  const signedIn = Boolean(session?.user)
  const homework = useQuery({ queryKey: ['course', 'homework'], queryFn: api.course.homework, enabled: signedIn })
  const review = useQuery({ queryKey: ['review', 'status'], queryFn: api.review.status, enabled: signedIn })
  const counts: Partial<Record<NavItem['key'], number>> = {
    homework: homework.data?.filter((h) => h.done < h.parts).length || undefined,
    review: review.data?.due || undefined,
  }

  useEffect(() => {
    if (!isPending && !session?.user && !unreachable) navigate('/login', { replace: true })
  }, [isPending, session, unreachable, navigate])

  if (isPending) {
    return (
      <div className="cl flex h-dvh items-center justify-center">
        <div className="w-72">
          <Skeleton />
        </div>
      </div>
    )
  }
  if (unreachable) return <SessionUnreachable />
  if (!session?.user) return <Navigate to="/login" replace />

  const name = user?.name ?? ''
  const firstName = name.split(/\s+/)[0] ?? name

  const page = (
    /* The last line of defence: a page that fails to draw says so instead of going white. */
    <PartBoundary
      resetKey={location.pathname}
      fallback={
        <div className="cl flex flex-1 items-center justify-center p-10">
          <p className="m-0 font-semibold text-class-wrong">✗ {t.errors.part}</p>
        </div>
      }
    >
      <Outlet />
    </PartBoundary>
  )

  return (
    <LocaleProvider locale={locale}>
      {chrome === 'none' ? (
        page
      ) : (
        <AppFrame>
          {chrome === 'side' ? (
            <Sidebar s={s} counts={counts} name={name} firstName={firstName} isAdmin={isAdmin} />
          ) : (
            <Rail s={s} counts={counts} name={name} active={inLessonHomework ? 'homework' : null} />
          )}
          {page}
        </AppFrame>
      )}
    </LocaleProvider>
  )
}

/** 256px on desktop; the bottom tab bar on a phone. */
function Sidebar({
  s,
  counts,
  name,
  firstName,
  isAdmin,
}: {
  s: ShellStrings
  counts: Partial<Record<NavItem['key'], number>>
  name: string
  firstName: string
  isAdmin: boolean
}) {
  return (
    <>
      {/* desktop */}
      <aside className="hidden h-full w-64 shrink-0 flex-col gap-5 overflow-y-auto border-r border-class-field bg-class-white px-3.5 py-5 desk:flex">
        <Link to="/panel" className="flex items-center gap-2.5 px-2 py-0.5 !text-class-ink">
          <LogoMark />
          <span className="font-class-display text-[18px] font-semibold">Diana</span>
        </Link>

        <nav aria-label={s.navLabel} className="flex flex-col gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                [
                  'flex min-h-[46px] items-center gap-3 rounded-class-btn px-3 font-bold',
                  isActive ? 'bg-class-field !text-class-sky-700' : '!text-class-ink-soft hover:bg-class-field-soft',
                ].join(' ')
              }
            >
              <Icon name={item.icon} />
              {s.nav[item.key]}
              {counts[item.key] !== undefined && (
                <span className="ml-auto text-class-label font-semibold text-class-ink-faint">{counts[item.key]}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto flex flex-col gap-2">
          {isAdmin && (
            <Link to="/studio/kursy" className="cl-link-quiet px-2 text-class-gloss">
              ‹ {s.backToStudio}
            </Link>
          )}
          <NavLink
            to="/panel/konto"
            className={({ isActive }) =>
              [
                'flex items-center gap-2.5 rounded-class-btn p-1.5 !text-class-ink',
                isActive ? 'bg-class-field-soft' : 'hover:bg-class-field-soft',
              ].join(' ')
            }
          >
            <Avatar name={name} />
            <span className="min-w-0 leading-tight">
              <span className="block truncate font-bold">{firstName}</span>
              <span className="block truncate text-class-label text-class-ink-soft">{s.accountHint}</span>
            </span>
          </NavLink>
        </div>
      </aside>

      {/* phone */}
      <nav
        aria-label={s.navLabel}
        className="flex shrink-0 border-t border-class-line bg-class-white px-1.5 pt-1 pb-[max(16px,env(safe-area-inset-bottom))] desk:hidden"
      >
        {NAV.filter((i) => i.tab).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              [
                'flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-class-label font-semibold',
                isActive ? '!text-class-sky-700' : '!text-class-ink-faint',
              ].join(' ')
            }
          >
            <Icon name={item.icon} stroke={1.8} />
            {s.tabs[item.tab!]}
          </NavLink>
        ))}
      </nav>
    </>
  )
}

/** The 76px icon rail for focused work. Hidden on phones — those screens are full-bleed. */
function Rail({
  s,
  counts,
  name,
  active,
}: {
  s: ShellStrings
  counts: Partial<Record<NavItem['key'], number>>
  name: string
  /** The item to light up when the URL alone would pick another (homework lives under /kurs). */
  active: NavItem['key'] | null
}) {
  return (
    <nav
      aria-label={s.navLabel}
      className="hidden h-full w-[76px] shrink-0 flex-col items-center gap-2 border-r border-class-line bg-class-white py-6 desk:flex"
    >
      <Link
        to="/panel"
        aria-label="Diana — start"
        className="mb-4 inline-flex h-[34px] w-[34px] items-center justify-center rounded-class-sm bg-class-gold font-class-display text-[15px] font-semibold !text-class-ink"
      >
        D
      </Link>
      {NAV.filter((i) => i.key !== 'lessons').map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          aria-label={counts[item.key] ? `${s.nav[item.key]}, ${counts[item.key]}` : s.nav[item.key]}
          title={s.nav[item.key]}
          className={({ isActive }) =>
            [
              'inline-flex h-12 w-12 items-center justify-center rounded-[14px]',
              (active ? active === item.key : isActive)
                ? 'bg-class-field-soft !text-class-sky-700'
                : '!text-class-ink-soft hover:bg-class-field-soft',
            ].join(' ')
          }
        >
          <Icon name={item.icon} />
        </NavLink>
      ))}
      <NavLink to="/panel/konto" aria-label={`${s.nav.account}: ${name}`} className="mt-auto">
        <Avatar name={name} size={40} className="!bg-class-field" />
      </NavLink>
    </nav>
  )
}
